

import { ExecuteSSISPackageActivityMetadataService } from 'app/authoring/component/activities/ssis-activity/ssis-activity-metadata.service';
import { Model } from 'app/model/backendmodel';
import { ExecuteSSISPackageActivity } from 'app/model/pipeline/ssis.activity';
import { AdapterContext, AzureKeyVault, Constants, ExecuteSSISPackageConnectionManagerParameterConfig, ExecuteSSISPackageParameterConfig, ExecuteSSISPackagePropertyOverrideConfig, InvokeSSISPackageActivity, Owned, ParameterConfig } from 'shell-models-lib';
import { ActivityAsyncAdapter } from './activity.adapter';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { ValueAdapter } from '../value.adapter';
import { akvToModel, akvToResource, isAKVObject } from '../linkedservices/azure-key-vault.util';
import { toParameterClass, toParameterObject } from '../linkedservices/parameter-config.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';

export abstract class SSISPackageActivityAdapter<ST extends InvokeSSISPackageActivity | ExecuteSSISPackageActivity, SU extends Model.InvokeSSISPackageActivity | Model.ExecuteSSISPackageActivity> extends ActivityAsyncAdapter<ST, SU> {

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toActivityParameterObject(activity: ST, scope: string): any {
        let configs = activity.parameters ? activity.parameters.filter((param) => param.scope === scope && param.name) : undefined;
        if (activity instanceof ExecuteSSISPackageActivity  && activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO) {
            const ssisEnvironment = ExecuteSSISPackageActivityMetadataService.findSsisEnvironmentFromCache(activity.ssisIr.name, activity.ssisFolder, activity.ssisEnvironment);
            if (scope === ExecuteSSISPackageParameterConfig.SCOPE_PROJECT) {
                const ssisProject = ExecuteSSISPackageActivityMetadataService.findSsisProjectWithParameterFromCache(activity.ssisIr.name, activity.ssisFolder, activity.ssisProject);
                if (ssisProject) {
                    configs = this.findToObjectParameterConfigs(configs, ssisProject.parameters, ssisEnvironment, false);
                }
            } else {
                const ssisPackage = ExecuteSSISPackageActivityMetadataService.findSsisPackageFromCache(activity.ssisIr.name, activity.ssisFolder, activity.ssisProject, activity.ssisPackage);
                if (ssisPackage) {
                    configs = this.findToObjectParameterConfigs(configs, ssisPackage.parameters, ssisEnvironment, false);
                }
            }
        }
        return this.toSsisParameterObject(configs);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toActivityConnectionManagerObject(activity: ST, scope: string): any {
        let configs = activity.connectionManagerParameters ? activity.connectionManagerParameters.filter((param) => param.scope === scope && param.connectionManager && param.name) : undefined;
        if (activity instanceof ExecuteSSISPackageActivity && activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO) {
            const ssisEnvironment = ExecuteSSISPackageActivityMetadataService.findSsisEnvironmentFromCache(activity.ssisIr.name, activity.ssisFolder, activity.ssisEnvironment);
            if (scope === ExecuteSSISPackageParameterConfig.SCOPE_PROJECT) {
                const ssisProject = ExecuteSSISPackageActivityMetadataService.findSsisProjectWithParameterFromCache(activity.ssisIr.name, activity.ssisFolder, activity.ssisProject);
                if (ssisProject) {
                    configs =
                        this.findToObjectParameterConfigs(configs, ssisProject.parameters, ssisEnvironment, true) as ExecuteSSISPackageConnectionManagerParameterConfig[];
                }
            } else {
                const ssisPackage = ExecuteSSISPackageActivityMetadataService.findSsisPackageFromCache(activity.ssisIr.name, activity.ssisFolder, activity.ssisProject, activity.ssisPackage);
                if (ssisPackage) {
                    configs =
                        this.findToObjectParameterConfigs(configs, ssisPackage.parameters, ssisEnvironment, true) as ExecuteSSISPackageConnectionManagerParameterConfig[];
                }
            }
        }
        if (!configs) {
            return undefined;
        }
        const groupConfigs = new Map<string, ExecuteSSISPackageConnectionManagerParameterConfig[]>();
        configs.forEach((config) => {
            if (groupConfigs.has(config.connectionManager)) {
                groupConfigs.get(config.connectionManager).push(config);
            } else {
                groupConfigs.set(config.connectionManager, [config]);
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const groupObject: any = {};
        groupConfigs.forEach((value, key) => {
            groupObject[key] = this.toSsisParameterObject(value);
        });
        return groupObject;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toPropertyOverrideClass(pObject: any, owner: Owned): any {
        if (!pObject) {
            return [];
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const unwrapper: any = {};
        for (const propertyName in pObject) {
            const val = pObject[propertyName]['value'];
            unwrapper[propertyName] = SecureStringReplacer.isSecureStringObject(val) ? val['value'] : val;
        }
        const configs = toParameterClass(unwrapper, owner) as ExecuteSSISPackagePropertyOverrideConfig[];
        configs.forEach((propertyOverride) => {
            propertyOverride.sensitive = pObject[propertyOverride.name]['isSensitive'] ? true : false;
            propertyOverride.fromJson = true;
        });
        return configs;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toPropertyOverrideObject(configs: ExecuteSSISPackagePropertyOverrideConfig[] | undefined): any {
        if (!configs) {
            return undefined;
        }

        // Temp save value
        const secConfigValue: string[] = [];
        configs.forEach((config) => secConfigValue.push(ValueAdapter.toResource(config, ExecuteSSISPackagePropertyOverrideConfig.VALUE)));

        // wrapper securec string object to fit toParameterObject input. Skip masked credential
        const secWrappedConfigs = configs
            .map((config) => {
                const val = ValueAdapter.toResource(config, ExecuteSSISPackagePropertyOverrideConfig.VALUE);
                if (config.sensitive && !isExpressionObject(val)) {
                    const secWrapperValue = {
                        type: 'SecureString',
                        value: val
                    };
                    ExecuteSSISPackagePropertyOverrideConfig.VALUE.setValue(config, secWrapperValue);
                }
                return config;
            });
        const pObject = toParameterObject(secWrappedConfigs as ParameterConfig[]);

        // recover value
        secConfigValue.forEach((val, index) => {
            if (isExpressionObject(val)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ExecuteSSISPackageParameterConfig.VALUE.setTemplateValue(configs[index], (val as any).value);
            } else {
                ExecuteSSISPackageParameterConfig.VALUE.setValue(configs[index], val);
            }
        });

        // wrapper to fit package json format
        configs.forEach((config) => {
            pObject[config.name] = {
                value: pObject[config.name],
                isSensitive: config.sensitive ? true : false
            };
        });
        return pObject;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toSsisParameterObject(configs: ExecuteSSISPackageParameterConfig[]): any {
        if (!configs) {
            return undefined;
        }

        // Temp save value
        const secConfigValue: string[] = [];
        configs.forEach((config) => {
            secConfigValue.push(ValueAdapter.toResource(config, ExecuteSSISPackageParameterConfig.VALUE));
        });

        // wrapper secure string or akv object to fit toParameterObject input. Skip masked credential
        const secWrappedConfigs = configs
            .map((config) => {
                const val = ValueAdapter.toResource(config, ExecuteSSISPackageParameterConfig.VALUE);
                if (config.sensitive && typeof val !== 'object') {
                    const secWrapperValue = {
                        type: 'SecureString',
                        value: val
                    };
                    ExecuteSSISPackageParameterConfig.VALUE.setValue(config, secWrapperValue);
                }
                if (config.value && config.value instanceof AzureKeyVault) {
                    const akv = akvToResource(config.value, this.context);
                    ExecuteSSISPackageParameterConfig.VALUE.setValue(config, akv);
                }
                return config;
            });
        const pObject = toParameterObject(secWrappedConfigs as ParameterConfig[]);

        // recover value
        secConfigValue.forEach((val, index) => {
            if (isExpressionObject(val)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ExecuteSSISPackageParameterConfig.VALUE.setTemplateValue(configs[index], (val as any).value);
            } else {
                ExecuteSSISPackageParameterConfig.VALUE.setValue(configs[index], val);
            }
        });

        // wrapper to fit package json format
        configs.forEach((config) => {
            pObject[config.name] = {
                value: pObject[config.name]
            };
        });
        return pObject;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toActivityConnectionManagerClass(pObject: any, owner: Owned, scope: string, context: AdapterContext): ExecuteSSISPackageConnectionManagerParameterConfig[] {
        if (!pObject) {
            return [];
        }
        const result: ExecuteSSISPackageConnectionManagerParameterConfig[] = [];
        for (const cmName in pObject) {
            const configs = this.toSsisParameterClass(pObject[cmName], owner, scope, context) as ExecuteSSISPackageConnectionManagerParameterConfig[];
            configs.forEach((config, i) => {
                config.index = i;
                config.connectionManager = cmName;
            });
            result.push(...configs);
        }
        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected toSsisParameterClass(pObject: any, owner: Owned, scope: string, context: AdapterContext): ExecuteSSISPackageParameterConfig[] {
        if (!pObject) {
            return [];
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const unwrapper: any = {};
        for (const propertyName in pObject) {
            unwrapper[propertyName] = pObject[propertyName]['value'];
        }
        const configs = toParameterClass(unwrapper, owner) as ExecuteSSISPackageParameterConfig[];
        configs.forEach((config) => {
            config.scope = scope;
            config.type = ExecuteSSISPackageParameterConfig.TYPE_NA; // Make default type as N/A. It will be set according to storage during activity inuit
            config.sensitive = SecureStringReplacer.isSecureStringObject(pObject[config.name]['value']);
            config.fromJson = true;
            if (config.sensitive) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ExecuteSSISPackageParameterConfig.VALUE.setValue(config, (config.value as any).value);
            }
            if (isAKVObject(config.value)) {
                config.value = akvToModel(config.value, context);
            }
        });
        return configs;
    }

    private findToObjectParameterConfigs(configs: ExecuteSSISPackageParameterConfig[], parameters: Model.SsisParameter[], environment: Model.SsisEnvironment, forCm: boolean):
        ExecuteSSISPackageParameterConfig[] {
        const objConfigs: ExecuteSSISPackageParameterConfig[] = [];
        const paramMap = new Map<string, Model.SsisParameter>();
        if (parameters) {
            parameters.forEach((param) => paramMap.set(param.name, param));
        }
        if (configs) {
            const variableMap = new Map<string, Model.SsisVariable>();
            if (environment && environment.variables) {
                environment.variables.forEach((variable) => {
                    variableMap.set(variable.name, variable);
                });
            }
            configs.forEach((config) => {
                let paramName = null;
                if (forCm) {
                    const cmConfig = config as ExecuteSSISPackageConnectionManagerParameterConfig;
                    paramName = 'CM.' + cmConfig.connectionManager + '.' + cmConfig.name;
                } else {
                    paramName = config.name;
                }
                if (this.shouldAddIntoObject(config, paramMap.get(paramName), variableMap.get(config.referencedVariable))) {
                    objConfigs.push(config);
                }
            });
        }
        return objConfigs;
    }

    private shouldAddIntoObject(config: ExecuteSSISPackageParameterConfig, cachedPatameter: Model.SsisParameter, cachedVariable: Model.SsisVariable): boolean {
        // Parameter config should be included in object if:
        // 1. Deletable parameter config
        // 2. Required parameter if:
        //    2.1 No environment variable is mapped
        //    2.2 Environment variable is mapped, but value is changed manually. follow case 5's logic
        // 3. Include SecureString if
        //    3.1 config is from json. It means customer has published password before. If it's "**********", it means keep previous SecureString. It should be handled by ADF
        //    3.2 it does not equal to "**********".
        // 4. Always include expression
        // 5. Different from origin value (designDefaultValue, defaultValue or environment variable)

        // Case 1
        if (config.deletable || !cachedPatameter) {
            return true;
        }

        // Case 2
        if (config.required) {
            if (!cachedVariable) {
                return true;
            }
        }

        const configValue = ValueAdapter.toResource(config, ExecuteSSISPackageParameterConfig.VALUE);

        // Case 3
        if (config.sensitive) {
            return config.fromJson ? true : configValue !== Constants.maskedCredential;
        }

        // Case 4
        if (isExpressionObject(configValue)) {
            return true;
        }

        // Figure out the original value
        let originalValue = cachedPatameter.designDefaultValue;
        if (config.valueSet) {
            if (config.valueType === ExecuteSSISPackageActivity.VALUE_TYPE_REFERENCE) {
                // use environment variable as original value
                if (cachedVariable) {
                    if (cachedVariable.sensitive) {
                        return configValue !== Constants.maskedCredential;
                    } else {
                        originalValue = cachedVariable.value;
                    }
                }
            } else {
                // use default value
                originalValue = cachedPatameter.defaultValue;
            }
        }

        // Case 5
        if (!originalValue && !configValue) {
            return false;
        } else if ((!originalValue && configValue) || (originalValue && !configValue)) {
            return true;
        }

        if (cachedPatameter.dataType === 'Boolean' && typeof configValue !== 'object') {
            return configValue.toLowerCase() !== originalValue.toLowerCase();
        } else if (configValue.toString() !== originalValue) {
            // normal case
            return true;
        }
        return false;
    }
}
//